import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../api/ApiProvider";
import {
  CreateInsuranceTransactionDto,
  GkvBillExportRequestDto,
  GkvBillExportResponseDto,
  GkvBillPrintResponseDto,
  GkvBillUpdateResponseDto,
  UpdateInsuranceTransactionDto,
} from "../../api-client";
import { insuranceTransactionKeys } from "./insurance-transaction.keys";

/**
 * NOTE: Global error/success handling is configured in src/stores/queryClient.ts
 * - MutationCache handles automatic error notifications via notificationStore
 * - MutationCache handles automatic success notifications via meta.successMessage
 * - No need to manually call notificationStore in individual mutations
 */

// ===========================
// GKV INSURANCE BILLING MUTATIONS
// ===========================

/**
 * Creates GKV insurance transactions (bills) from payer groups.
 *
 * Replaces: useGkvBillSave hook that called store-based createTransaction in a loop.
 * Used by: GKV bill creation step — after user confirms payer groups.
 *
 * Note: The backend `createTransaction` endpoint is generic for both patient and insurance.
 * This mutation wraps it specifically for GKV bill creation with proper cache invalidation.
 */
export const useCreateGkvBill = () => {
  const { transaction } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateInsuranceTransactionDto) => transaction.createTransaction(dto),
    meta: {
      successMessage: "Abrechnung erfolgreich erstellt",
      errorMessage: "Fehler beim Erstellen der Abrechnung",
    },
  });
};

/**
 * Updates a GKV bill (notes, rxIds, payer, billing centers, etc.).
 *
 * Replaces: store-based updateInsuranceTransaction calls.
 * Used by: GKV bill edit page — auto-save or manual save.
 *
 * Invalidates only the specific bill's cache, not all bills.
 */
export const useUpdateGkvBill = () => {
  const { transaction } = useApi();
  const qc = useQueryClient();

  return useMutation<
    GkvBillUpdateResponseDto,
    Error,
    { billId: string; dto: UpdateInsuranceTransactionDto }
  >({
    mutationFn: ({ billId, dto }) => transaction.updateGkvBill(billId, dto),
    onSuccess: (_data, { billId }) => {
      qc.invalidateQueries({
        queryKey: insuranceTransactionKeys.gkvBillEdit.forBill(billId),
      });
    },
    meta: {
      errorMessage: "Fehler beim Aktualisieren der Abrechnung",
    },
  });
};

/**
 * Prints a GKV bill — generates PDFs (paper center receipt + bill PDF).
 *
 * Replaces: store-based printDirectBillingDocument calls.
 * Used by: GKV bill edit page — "Drucken" button.
 *
 * Invalidates only the printed bill's cache so the PDF shows in the edit modal.
 * Sequential calls won't trigger a full refetch of all bills.
 */
export const usePrintGkvBill = () => {
  const { transaction } = useApi();
  const qc = useQueryClient();

  return useMutation<GkvBillPrintResponseDto, Error, { billId: string }>({
    mutationFn: ({ billId }) => transaction.printGkvBill(billId),
    onSuccess: (_data, { billId }) => {
      qc.invalidateQueries({
        queryKey: insuranceTransactionKeys.gkvBillEdit.forBill(billId),
      });
    },
    meta: {
      successMessage: "Dokumente erfolgreich erstellt",
      errorMessage: "Fehler beim Erstellen der Dokumente",
    },
  });
};

/**
 * Exports GKV bills — encrypts EDIFACT and sends to data center via email.
 *
 * Replaces: store-based exportInsuranceBills calls.
 * Used by: GKV bill edit page — "Exportieren" button.
 *
 * Error isolation: individual bill failures are in the response results,
 * the mutation itself only rejects on network/server errors.
 *
 * Invalidates each exported bill's cache individually.
 */
export const useExportGkvBills = () => {
  const { transaction } = useApi();
  const qc = useQueryClient();

  return useMutation<GkvBillExportResponseDto, Error, GkvBillExportRequestDto>({
    mutationFn: (dto) => transaction.exportGkvBills(dto),
    onSuccess: (_data, dto) => {
      for (const billId of dto.billIds) {
        qc.invalidateQueries({
          queryKey: insuranceTransactionKeys.gkvBillEdit.forBill(billId),
        });
      }
    },
    meta: {
      errorMessage: "Fehler beim Exportieren der Abrechnungen",
    },
  });
};
